import { derived, get, type Readable, writable } from 'svelte/store';
import { parseResponse } from '$lib/client/responseHelpers';

import type { ChatHistoryItem, UserCurrent } from '@api/types';
import { getChatHistory } from '@api/user/chat-history';
import { getClosedReasonText } from '@components/room/functions';
import { userStore } from '@components/user/store';
import LL from '@i18n/i18n-svelte';
import type { RoomClosedReason } from '@shared/admin/room';

import type { Store, StoreState } from './types';

const initialValues: StoreState = {
    chatHistory: [],
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => ({
            ...state,
            ...data,
        }));
    };

    const load = async (force?: boolean, limit?:number) => {
        const storeState = get(store);
        if ((force && storeState !== initialValues && storeState.nextCursor) || storeState === initialValues) {
            const listResponse = await getChatHistory(limit, storeState.nextCursor);
            const data = parseResponse(listResponse);
            if (data) {
                updateStore({
                    chatHistory: [...storeState.chatHistory, ...data.chatHistory],
                    nextCursor: data.nextCursor,
                });
            }
        }
    };

    return {
        update,
        set,
        subscribe,
        load,
        add: (item: ChatHistoryItem) => {
            const state = get(store);
            updateStore({
                chatHistory: [item, ...state.chatHistory],
                nextCursor: state.nextCursor,
            });
        },
        addFavorite: (id: string) => {
            const state = get(store);
            for (const item of state.chatHistory) {
                if (item.participant.id === id) {
                    item.participant.inMyFavorites = true;
                }
            }
        },
        deleteFavorite: (id: string) => {
            const state = get(store);
            for (const item of state.chatHistory) {
                if (item.participant.id === id) {
                    item.participant.inMyFavorites = false;
                }
            }
        },

        changeIsBlocked: (id: string, val: boolean) => {
            const state = get(store);

            const chatHistory = state.chatHistory.map((item) => {
                if (item.participant.id === id) {
                    return {
                        ...item,
                        participant: {
                            ...item.participant,
                            isBlocked: val,
                            inMyFavorites: val ? false : item.participant.inMyFavorites,
                        },
                    };
                }

                return item;
            });

            updateStore({ chatHistory });
        },

        changeIsBlockedBy: (id: string, val: boolean) => {
            const state = get(store);

            const chatHistory = state.chatHistory.map((item) => {
                if (item.participant.id === id) {
                    return {
                        ...item,
                        participant: {
                            ...item.participant,
                            isBlockedBy: val,
                        },
                    };
                }

                return item;
            });

            updateStore({ chatHistory });
        },
    };
}

export const chatHistoryStore = createStore();

export const lastClosedReasonText: Readable<string> = derived(
    [chatHistoryStore, userStore, LL],
    ([$store, $userStore, $LL]) => {
        const lastChat = $store.chatHistory[0];
        if (!lastChat || !$userStore.user) {
            return '';
        }

        return getClosedReasonText($LL.roomClosedReason, {
            user: $userStore.user,
            closedReason: lastChat.closedReason as RoomClosedReason | undefined,
            closedBy: lastChat.closedBy,
            isCall: lastChat.isCall,
            endedAt: lastChat.endedAt,
        });
    }
);
